import { differenceInMilliseconds, differenceInMinutes, differenceInSeconds, getDate, getMonth, getYear, intervalToDuration, set } from "date-fns";
import { useEffect, useState } from "react";

import { DATE_FORMATS_FNS } from "components";
import { useSelectedLocation } from "components/DealersAndLocationsDropdown/hooks";
import "modules/Appointments/components/Timer/Timer.scss";
import { formatDate, isDateAfter } from "utils/dateHelperFunctions";

type FormattedSlot = {
  timeLeft: string;
  color: string;
};

const Timerslot = ({ timeLeft, color }: FormattedSlot) => <div className={`Timerslot ${color}`}>{timeLeft}</div>;

export const Timer = () => {
  const { selectedLocation } = useSelectedLocation();
  const [formattedTimeSlots, setFormattedTimeSlots] = useState<FormattedSlot[]>([]);

  useEffect(() => {
    const getFormattedTimeSlots = () => {
      const currentTime = new Date();

      if (!selectedLocation?.timeslots) {
        setFormattedTimeSlots([]);
        return;
      }

      const formattedTimeSlots = selectedLocation.timeslots
        .filter(time => time.active)
        .sort((a, b) => differenceInMilliseconds(new Date(b.deadline), new Date(a.deadline)))
        .map(slot => {
          const deadline = set(new Date(slot.deadline), {
            year: getYear(currentTime),
            month: getMonth(currentTime),
            date: getDate(currentTime)
          });

          if (isDateAfter(currentTime, deadline)) return { color: "red", timeLeft: "00:00" };

          const secondsDiff = differenceInSeconds(deadline, currentTime);
          const minutesDiff = differenceInMinutes(deadline, currentTime);

          if (secondsDiff <= 0) return { color: "red", timeLeft: "00:00" };

          if (secondsDiff <= 60) {
            return { color: "blue", timeLeft: `-00:${secondsDiff.toString().padStart(2, "0")}` };
          }
          if (minutesDiff < 30) {
            const diff = intervalToDuration({ start: currentTime, end: deadline });
            const minutes = diff.minutes?.toString().padStart(2, "0") ?? "00";
            const seconds = diff.seconds?.toString().padStart(2, "0") ?? "00";
            return { color: "blue", timeLeft: `-${minutes}:${seconds}` };
          }

          return {
            color: "green",
            timeLeft: formatDate(slot.deadline, DATE_FORMATS_FNS.hoursMinutes)
          };
        });

      setFormattedTimeSlots(formattedTimeSlots);
    };

    getFormattedTimeSlots();
    const intervalId = setInterval(getFormattedTimeSlots, 1000);
    return () => clearInterval(intervalId);
  }, [selectedLocation?.id]);

  if (!formattedTimeSlots.length) {
    return null;
  }

  return (
    <div className="Timer">
      {formattedTimeSlots.map((s, k) => {
        return <Timerslot key={k} color={s.color} timeLeft={s.timeLeft} />;
      })}
    </div>
  );
};
